import React, { FC, ReactElement, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Spin, Input } from "antd";
import {
  ShoppingOutlined,
  SearchOutlined,
  WarningFilled,
  CheckCircleFilled,
  RightOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../../../constants/urlConstants";
import "./MyStock.css";

interface StockItem {
  perfumeId: number;
  productTitle: string;
  brand: string;
  price: number;
  distributorPrice: number;
  stockQuantity: number;
  minThreshold: number;
  category: string;
  description: string;
  filename: string;
}

const MyStock: FC = (): ReactElement => {
  const history = useHistory();

  const [items, setItems]     = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState("");
  const [error, setError]     = useState("");

  const role          = localStorage.getItem("role");
  const isDistributor = role === "3";
  const distId        = localStorage.getItem("distId");

  useEffect(() => {
    if (!distId) return;
    setLoading(true);
    axios
      .get(`${BASE_URL}/api/v1/inventory/distributor/${distId}`)
      .then((res) => setItems(res.data || []))
      .catch(() => setError("Failed to load stock. Please try again."))
      .finally(() => setLoading(false));
  }, [distId]);

  const filtered  = items.filter((item) =>
    item.productTitle.toLowerCase().includes(search.toLowerCase()) ||
    item.brand.toLowerCase().includes(search.toLowerCase()) ||
    item.category.toLowerCase().includes(search.toLowerCase())
  );

  const lowStock = items.filter((i) => i.stockQuantity <= i.minThreshold).length;
  const totalQty = items.reduce((s, i) => s + i.stockQuantity, 0);

  const goToProduct = (id: number) => history.push(`/product/${id}`);

  if (loading) {
    return (
      <div className="ms-page">
        <div className="ms-center"><Spin size="large" /></div>
      </div>
    );
  }

  return (
    <div className="ms-page">

      {/* ── HEADER ── */}
      <div className="ms-header">
        <ShoppingOutlined className="ms-header-icon" />
        <span className="ms-header-title">Distributor Stock</span>
        <span className="ms-header-count">{items.length} products</span>
      </div>

      {/* ── SUMMARY STRIP ── */}
      {items.length > 0 && (
        <div className="ms-summary">
          <div className="ms-stat">
            <span className="ms-stat-val">{items.length}</span>
            <span className="ms-stat-label">Total Products</span>
          </div>
          <div className="ms-stat-divider" />
          <div className="ms-stat">
            <span className="ms-stat-val">{totalQty}</span>
            <span className="ms-stat-label">Total Units</span>
          </div>
          <div className="ms-stat-divider" />
          <div className={`ms-stat ${lowStock > 0 ? "warn" : ""}`}>
            <span className="ms-stat-val">{lowStock}</span>
            <span className="ms-stat-label">Low Stock</span>
          </div>
        </div>
      )}

      {/* ── SEARCH ── */}
      {items.length > 0 && (
        <div className="ms-search-wrap">
          <Input
            prefix={<SearchOutlined style={{ color: "#aaa" }} />}
            placeholder="Search by product, brand or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            className="ms-search"
          />
        </div>
      )}

      {/* ── ERROR ── */}
      {error && <div className="ms-error">{error}</div>}

      {/* ── EMPTY ── */}
      {!loading && !error && items.length === 0 && (
        <div className="ms-empty">
          <InboxOutlined className="ms-empty-icon" />
          <p className="ms-empty-title">No stock assigned</p>
          <p className="ms-empty-sub">
            Your inventory will appear here once products are assigned to you.
          </p>
        </div>
      )}

      {/* ── NO SEARCH RESULTS ── */}
      {!loading && filtered.length === 0 && items.length > 0 && (
        <div className="ms-empty">
          <SearchOutlined className="ms-empty-icon" />
          <p className="ms-empty-title">No products match "{search}"</p>
        </div>
      )}

      {/* ── PRODUCT GRID ── */}
      <div className="ms-grid">
        {filtered.map((item) => {
          const isLow      = item.stockQuantity <= item.minThreshold;
          const stockPct   = Math.min(
            (item.stockQuantity / Math.max(item.minThreshold * 4, 1)) * 100,
            100
          );
          const savings    = item.price - item.distributorPrice;
          const savingsPct = Math.round((savings / item.price) * 100);

          return (
            <div
              key={item.perfumeId}
              className={`ms-card ${isLow ? "low-stock" : ""}`}
              onClick={() => goToProduct(item.perfumeId)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && goToProduct(item.perfumeId)}
            >
              {/* Low stock banner */}
              {isLow && (
                <div className="ms-card-alert">
                  <WarningFilled /> Low Stock
                </div>
              )}

              {/* Image */}
              <div className="ms-card-img-wrap">
                <img
                  src={item.filename || "/no-image.png"}
                  alt={item.productTitle}
                  className="ms-card-img"
                />
              </div>

              {/* Body */}
              <div className="ms-card-body">
                <div className="ms-card-info">
                  <div className="ms-card-category">{item.category}</div>
                  <div className="ms-card-title">{item.productTitle}</div>
                  <div className="ms-card-brand">{item.brand}</div>
                </div>

                {/* ✅ Price row — only for NON-distributors (retailers) */}
                {isDistributor && (
                  <div className="ms-card-prices">
                    <div className="ms-price-block">
                      <span className="ms-price-label">Your Price</span>
                      <span className="ms-dist-price">₹{item.distributorPrice}</span>
                    </div>

                    <div className="ms-price-divider" />

                    <div className="ms-price-block">
                      <span className="ms-price-label">MRP</span>
                      <span className="ms-mrp">₹{item.price}</span>
                    </div>

                    {savingsPct > 0 && (
                      <span className="ms-savings-badge">{savingsPct}% off</span>
                    )}
                  </div>
                )}

                {/* Stock level */}
                <div className="ms-stock-section">
                  <div className="ms-stock-row">
                    <span className="ms-stock-label">
                      {isLow
                        ? <><WarningFilled className="ms-warn-icon" /> Stock Alert</>
                        : <><CheckCircleFilled className="ms-ok-icon" /> In Stock</>
                      }
                    </span>
                    <span className={`ms-stock-qty ${isLow ? "low" : ""}`}>
                      {item.stockQuantity} units
                    </span>
                  </div>

                  {/* Stock bar */}
                  <div className="ms-stock-bar-bg">
                    <div
                      className={`ms-stock-bar-fill ${isLow ? "low" : ""}`}
                      style={{ width: `${stockPct}%` }}
                    />
                  </div>

                  <div className="ms-stock-threshold">
                    Min threshold: {item.minThreshold} units
                  </div>
                </div>

                {/* Footer */}
                <div className="ms-card-footer">
                  <span className="ms-view-label">View Product</span>
                  <RightOutlined className="ms-arrow" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MyStock;